<template>
  <div :class="{ 'game-card--info': showInfo }" class="game-card">
    <img
      v-if="comingSoon"
      alt="coming-soon"
      class="card-coming-soon no-pointer-events"
      loading="lazy"
      src="/images/icons/coming-soon-sm.png?v=1"
    />
    <div
      :class="{ 'coming-soon-grayed': comingSoon }"
      class="game-card-wrapper"
      v-on="showInfo ? { click: hideInfo } : {}"
    >
      <img
        :loading="onFirstPage ? 'eager' : 'lazy'"
        :src="image"
        alt="MaxQuest Game Card"
        class="game-card-cover"
        height="100%"
        width="100%"
      />

      <div
        v-if="showRibbon"
        :class="{
          'game-card-ribbon--fastplay': isFastPlay,
          'game-card-ribbon--pvp': !isFastPlay,
        }"
        class="game-card-ribbon"
      >
        <template v-if="isFastPlay">
          <IconFastplay class="fastplay" />
          <div class="game-card-ribbon-text">FASTPLAY</div>
        </template>
        <template v-else>
          <IconPvp class="pvp" />
          <div class="game-card-ribbon-text">PLAYER vs PLAYER</div>
        </template>
      </div>

      <InfoIcon
        v-if="!noInfo"
        :class="{ 'game-card-info-button--with-ribbon': showRibbon }"
        class="game-card-info-button"
        @click="toggleInfo"
      />
      <v-icon class="game-card-info-close" color="#fff">mdi-close</v-icon>

      <div class="game-card-info">
        <div class="wrapper">
          <div class="game-card-info-title">
            <span>{{ title }}</span>
          </div>
          <div v-for="(bullet, index) of gameInfo" :key="index" class="game-card-info-bullet">
            <component :is="`${bullet.svgIcon}Icon`" class="game-card-info-icon" />
            <span v-html="bullet.text" />
          </div>
        </div>
      </div>

      <div class="game-card-cta" @click="handleCardClick">
        <div class="game-card-cta-text">Play now</div>
      </div>
    </div>
    <div :class="{ 'no-pointer-events': showInfo }" class="clickable-areas">
      <div v-if="!noInfo" class="area area-info" @click="toggleInfo" />
      <div class="area area-cta" @click="handleCardClick" />
    </div>
  </div>
</template>

<script>
  import EventBus from '@/plugins/eventBus';
  import { gameInfos } from '@/data/games';
  import IconFastplay from '@/assets/images/svg/icons/fastplay.svg';
  import IconPvp from '@/assets/images/svg/icons/pvp.svg';
  import InfoIcon from '@/assets/images/svg/icons/info-icon.svg';

  export default {
    name: 'GameCard',

    emits: ['item-clicked'],

    components: {
      InfoIcon,
      IconPvp,
      IconFastplay,
      PalmIcon: async () => await import('@/assets/images/svg/icons/palm.svg'),
      AchievementIcon: async () => await import('@/assets/images/svg/icons/achievement.svg'),
      DefeatIcon: async () => await import('@/assets/images/svg/icons/defeat.svg'),
      RocketIcon: async () => await import('@/assets/images/svg/icons/rocket2.svg'),
      AlienIcon: async () => await import('@/assets/images/svg/icons/alien2.svg'),
      TrophyIcon: async () => await import('@/assets/images/svg/icons/trophy.svg'),
      MonkeyIcon: async () => await import('@/assets/images/svg/icons/monkey2.svg'),
      DiamondIcon: async () => await import('@/assets/images/svg/icons/diamond.svg'),
      AimIcon: async () => await import('@/assets/images/svg/icons/aim.svg'),
      StrikeIcon: async () => await import('@/assets/images/svg/icons/strike.svg'),
      FragmentsIcon: async () => await import('@/assets/images/svg/icons/fragments.svg'),
      UseIcon: async () => await import('@/assets/images/svg/icons/use.svg'),
      ReceiveIcon: async () => await import('@/assets/images/svg/icons/receive.svg'),
      TakeDownIcon: async () => await import('@/assets/images/svg/icons/take-down.svg'),
      CriticalIcon: async () => await import('@/assets/images/svg/icons/critical.svg'),
      CubeIcon: async () => await import('@/assets/images/svg/icons/cube.svg'),
      SpecialWeaponIcon: async () => await import('@/assets/images/svg/icons/special-weapon.svg'),
    },

    props: {
      title: {
        type: String,
        required: true,
      },
      image: {
        type: String,
        required: true,
      },
      category: {
        type: String,
        required: true,
      },
      isFastPlay: {
        type: Boolean,
        default: false,
      },
      showRibbon: {
        type: Boolean,
        default: false,
      },
      comingSoon: {
        type: Boolean,
        default: false,
      },
      onFirstPage: {
        type: Boolean,
        default: false,
      },
      gameType: {
        type: String,
        required: true,
      },
      customInfo: {
        type: Object,
        default: null,
      },
      noInfo: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      showInfo: false,
    }),

    computed: {
      gameInfo() {
        if (this.customInfo) return this.customInfo;
        const config = gameInfos[this.gameType];
        if (!config) return null;
        return this.isFastPlay ? config.fastplay : config.pvp;
      },

      path() {
        return this.$route.path;
      },
    },

    watch: {
      path() {
        this.showInfo = false;
      },
    },

    created() {
      EventBus.on('toggle-card-info', this.hideInfo);
    },

    beforeDestroy() {
      EventBus.off('toggle-card-info', this.hideInfo);
    },

    methods: {
      hideInfo() {
        this.showInfo = false;
      },

      async toggleInfo() {
        if (this.noInfo) return;

        const newValue = !this.showInfo;
        EventBus.emit('toggle-card-info');
        await this.$nextTick();
        this.showInfo = newValue;
      },

      handleCardClick() {
        this.$emit('item-clicked');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .game-card {
    container-type: inline-size;
    container-name: game-card;
    position: relative;
    border-radius: 10px;
    cursor: pointer;
    overflow: hidden;
    min-height: 126px;
    width: 100%;
    max-width: 264px;
    transition: all 0.3s ease;

    @media screen and (min-width: 600px) {
      max-width: 364px;
    }

    @media screen and (min-width: 1024px) {
      max-width: none;
    }

    &-wrapper {
      display: flex;
      aspect-ratio: 6/5;
    }

    &-cta {
      container-type: inline-size;
      container-name: game-card-cta;
      position: absolute;
      z-index: 2;
      margin-left: 50%;
      transform: translateX(-50%);
      background: #f8bf1e;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        filter: brightness(105%);
      }

      &:active {
        filter: brightness(97%);
      }

      &-text {
        font-family: 'Inter', sans-serif;
        color: #191919;
        font-weight: 800;
        text-transform: uppercase;
      }
    }

    &-ribbon {
      position: absolute;
      top: 0;
      width: 100%;
      height: 10%;
      background: rgba(60, 20, 91, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;

      &--fastplay {
        background: rgba(161, 38, 237, 0.85);
      }

      &--pvp {
        div {
          letter-spacing: 0.6px;
        }
      }

      svg {
        &.fastplay {
          max-width: 4%;
        }

        &.pvp {
          max-width: 9%;
        }
      }

      &-text {
        display: flex;
        align-items: center;
        padding: 1px 0;
        height: 100%;
        font-size: clamp(10px, 2.1vw, 14px);
        font-weight: 900;
        letter-spacing: 0.28px;
        color: #fff;
        text-align: center;
        font-family: 'Inter', sans-serif;
      }
    }

    &-info {
      width: 100%;
      height: 100%;
      cursor: pointer;
      position: absolute;
      inset: 0;
      color: #fff;
      text-align: left;
      border-radius: 10px;

      &:hover {
        box-shadow: 0 0 0 2px inset var(--v-gold-base);
      }

      .wrapper {
        padding: 8px;
        opacity: 0;
        background-color: #191919;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        transition: all 0.3s ease;

        @media screen and (min-width: 600px) {
          padding: 12px;
        }

        @media screen and (min-width: 1440px) {
          padding: 16px;
        }
      }

      &-title {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-weight: 600;
      }

      &-bullet {
        font-weight: 400;

        svg {
          margin-right: 4px;
          width: 10px;
          transform: translateY(1px);
        }
      }

      &::v-deep strong {
        color: var(--v-gold-base);
        caret-color: var(--v-gold-base);
      }

      @media screen and (min-width: 600px) {
        &-title {
          font-size: 16px;
          //margin-bottom: 6px;

          .v-icon {
            font-size: 18px;
          }
        }

        &-bullet {
          font-size: 11px;

          img {
            width: 9px;
          }
        }
      }

      @media screen and (min-width: 720px) {
        &-title {
          font-size: 18px;
          //margin-bottom: 8px;

          .v-icon {
            font-size: 20px;
          }
        }

        &-bullet {
          font-size: 14px;

          img {
            width: 10px;
          }
        }
      }

      @media screen and (min-width: 800px) {
        &-title {
          font-size: 14px;
          margin-bottom: 0;

          .v-icon {
            font-size: 14px;
          }
        }

        &-bullet {
          font-size: 10px;

          img {
            width: 8px;
          }
        }
      }

      @media screen and (min-width: 820px) {
        &-title {
          font-size: clamp(14px, 2vw, 20px);
          //margin-bottom: 6px;

          .v-icon {
            font-size: clamp(14px, 1.6vw, 20px);
          }
        }

        &-bullet {
          font-size: 11px;
        }
      }

      @media screen and (min-width: 1024px) {
        &-title {
          font-size: clamp(14px, 1.5vw, 20px);
          //margin-bottom: 2px;
        }

        &-bullet {
          font-size: clamp(10px, 1.1vw, 14px);
        }
      }

      @media screen and (min-width: 1280px) {
        &-bullet img {
          width: 10px;
        }
      }

      @media screen and (min-width: 1440px) {
        &-title {
          font-size: clamp(16px, 1.7vw, 22px);
          //margin-bottom: 8px;

          .v-icon {
            font-size: 24px;
          }
        }

        &-bullet {
          font-size: clamp(13px, 1.2vw, 16px);
        }
      }

      @media screen and (min-width: 1540px) {
        &-title {
          font-size: clamp(18px, 1.7vw, 26px);
        }
      }

      @media screen and (min-width: 1660px) {
        &-title {
          font-size: clamp(20px, 1.7vw, 28px);
        }

        &-bullet {
          font-size: clamp(14px, 1.2vw, 18px);

          img {
            width: 12px;
          }
        }
      }
    }

    .game-card-info-close {
      display: none;
      z-index: 1;
    }

    .game-card-info-close,
    .game-card-info-button {
      position: absolute;
      cursor: pointer;
      z-index: 3;
    }

    &--info {
      .game-card-info {
        &:hover {
          box-shadow: none;

          .wrapper {
            box-shadow: 0 0 0 2px inset #fff;
          }
        }

        .wrapper {
          opacity: 1;
        }
      }

      .game-card-info-button {
        display: none;
      }

      .game-card-info-close {
        display: inherit;
      }
    }
  }

  @container game-card (min-width: 100px) {
    .game-card-cta {
      border-radius: 4px;
      bottom: 6px;
      width: calc(100% - 16px);
      aspect-ratio: 161/26;
    }

    .game-card-info-close,
    .game-card-info-button {
      font-size: min(10cqi, 18px);
      margin-top: 6px;
      right: 6px;
      width: min(36px, 10cqi);
      height: min(36px, 10cqi);
    }

    .game-card-info-button--with-ribbon {
      transform: translateY(7cqi);
    }

    .game-card-info-title {
      font-size: 6.5cqi;
    }

    .game-card-ribbon-text {
      font-size: 4.5cqi;
    }

    .game-card-ribbon svg.pvp {
      max-width: 8cqi;
    }

    .game-card-ribbon svg.fastplay {
      max-width: 3cqi;
    }

    .game-card-info-bullet {
      font-size: 5.5cqi;
    }
  }

  @container game-card (min-width: 200px) {
    .game-card-info-close,
    .game-card-info-button {
      font-size: min(11cqi, 22px);
      margin-top: 10px;
      right: 10px;
    }
  }

  @container game-card (min-width: 260px) {
    .game-card-cta {
      bottom: 8px;
    }

    .game-card-info-bullet {
      display: flex;
      align-items: flex-start;
      font-size: min(24px, 5cqi);
      line-height: min(32px, 8cqi);

      &::v-deep strong {
        font-size: min(30px, 6cqi);
      }

      svg {
        width: 6cqi;
        min-width: 20px;
        max-width: 20px;
        transform: translateY(5px);
      }
    }

    .game-card-info-title {
      font-size: min(32px, 7.5cqi);
    }
  }

  @container game-card (min-width: 300px) {
    .game-card-cta {
      border-radius: 6px;
      bottom: 12px;
      width: min(60%, 228px);
      aspect-ratio: 228/48;
    }
  }

  @container game-card (min-width: 380px) {
    .game-card-cta {
      bottom: 24px;
    }

    .game-card-info-bullet {
      svg {
        transform: translateY(4px);
        margin-right: 6px;
      }
    }

    .game-card-info-button--with-ribbon {
      transform: translateY(8cqi);
    }

    .game-card-ribbon-text {
      font-size: 3.5cqi;
    }

    .game-card-ribbon svg.pvp {
      max-width: 6cqi;
    }

    .game-card-ribbon svg.fastplay {
      max-width: 2.3cqi;
    }
  }

  @container game-card (min-width: 450px) {
    .game-card-info-bullet svg {
      transform: translateY(8px);
    }
  }

  @container game-card-cta (min-width: 90px) {
    .game-card-cta-text {
      font-size: clamp(10px, 7cqi, 16px);
      font-weight: 700;
    }
  }

  @container game-card-cta (min-width: 230px) {
    .game-card-cta-text {
      font-weight: 800;
    }
  }

  .coming-soon-grayed::v-deep {
    filter: blur(5px) !important;
    overflow: hidden;

    .v-responsive__sizer {
      background-color: rgba(136, 121, 184, 0.4);
    }
  }

  .card-coming-soon {
    position: absolute;
    max-width: 70%;
    right: 50%;
    top: 50%;
    transform: translate(50%, -50%);
    z-index: 4;
  }

  .clickable-areas {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;

    .area {
      width: 100%;
    }

    .area-cta {
      height: 100%;
    }

    .area-info {
      height: 30%;

      & + .area-cta {
        height: 70%;
      }
    }
  }
</style>
